// ✅ src/App.js
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CustomThemeProvider } from "./theme/CustomThemeProvider";
import Layout from "./components/Layout";
import { GoogleOAuthProvider } from "@react-oauth/google";
import MaintenancePage from "./components/MaintenancePage";
import AboutUs from "./components/AboutUs";
import RecipeSearchPage from "./components/RecipeSearchPage";
import Login from "./components/Login";
import Register from "./components/Register";
import RecipeDetails from "./components/RecipeDetails";
import UserDashboard from "./components/client/UserDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import CheckInventory from "./components/client/CheckInventory";
import AddProductInventory from "./components/client/AddProductInventory";
import HouseholdProfile from "./components/client/HouseholdProfile";
import ShoppingList from "./components/client/ShoppingList";
import ShoppingListsIndex from "./components/client/ShoppingListsIndex";
import SubscriptionPage from "./components/client/SubscriptionPage";
import SubscriptionStatus from "./components/common/SubscriptionStatus";
import MealPlan from "./components/client/MealPlan";
import RecipesUser from "./components/client/RecipesUser";
import AddRecipeUser from "./components/client/AddRecipeUser";
import UpdateRecipeUser from "./components/client/UpdateRecipeUser";
import ClientLandingPage from "./components/client/ClientLandingPage";
import NotFound from "./components/NotFound";
import ScrollToTop from "./ScrollToTop";
import PrivacyPage from "./components/PrivacyPage";
import QuickRecipePage from "./components/QuickRecipePage";
import ReviewsPage from "./components/ReviewsPage";
import ResetPassword from "./components/ResetPassword";
import PaymentSuccess from "./components/PaymentSuccess";
import MobileBottomNav from "./components/MobileBottomNav";
import CustomShoppingList from "./components/CustomShoppingList";
import FacebookCallback from "./components/FacebookCallback";
import { pathLangFrom, isSupportedPathLang, canonicalI18n } from "./utils/lang";
import { useTranslation } from "react-i18next";
import { UserProvider } from "./context/UserContext";
import PlansPage from "./components/client/PlansPage";
import ReturnFromInstacart from "./components/modal/ReturnFromInstacart";
import AnalyticsTracker from "./components/Analytics/AnalyticsTracker";
import AdminGrowthDashboard from "./components/admin/AdminGrowthDashboard";
import PriceIntelligencePage from "./pages/PriceIntelligencePage";
import FollowedProductsPage from "./pages/FollowedProductsPage";
import BudgetRecipesPage from "./pages/BudgetRecipesPage";
import { HelmetProvider } from "react-helmet-async";
import OnboardingTour from "./components/OnboardingTour";
import { useAuth } from "./context/AuthContext";
import ConsentBanner from "./components/ConsentBanner";
import { getConsent, grantAnalytics, denyAnalytics } from "./utils/analytics/consent";
import { initGtag, getGaMeasurementId } from "./utils/analytics/gtag";
import MobileSocialAuthBridge from "./components/auth/MobileSocialAuthBridge";

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "713874922149-cn2blkikd2h64sekbilre7lhquh4p58k.apps.googleusercontent.com";

const MAINTENANCE_MODE = false;
const ONBOARDING_ROUTE_PATTERNS = [
  /^\/[a-z]{2}\/client\/dashboard\/?$/i,
  /^\/[a-z]{2}\/client\/meal\/plan\/?$/i,
  /^\/[a-z]{2}\/subscription\/?$/i,
  /^\/[a-z]{2}\/client\/inventory\/add\/product\/inventory\/?$/i,
];

function isOnboardingRouteAllowed(pathname) {
  return ONBOARDING_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname || ""));
}

const App = () => {
  const location = useLocation();
  const {
    user,
    loading: authLoading,
    hasCompletedOnboarding,
    markOnboardingCompleted,
    consumeOnboardingForceOpen,
  } = useAuth() || {};

  // ✅ normalize path (δέχεται /el και /el/)
  const normalizedPath = (location.pathname || "/").replace(/\/+$/, "") || "/";

  // ✅ παίρνει το πρώτο segment: /el/... -> el
  const match = normalizedPath.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i);
  const seg = match ? match[1] : null;

  const [analyticsConsent, setAnalyticsConsent] = useState(() => getConsent());

  const { t, i18n } = useTranslation();
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const gaMeasurementId = getGaMeasurementId();

  // ✅ sync i18n language από το URL
  useEffect(() => {
    if (!seg) return;
    const canon = canonicalI18n(seg);
    if (i18n.language !== canon) {
      i18n.changeLanguage(canon);
      localStorage.setItem("language", canon);
    }
  }, [seg, i18n]);

  useEffect(() => {
    if (analyticsConsent !== "granted") return;
    if (!gaMeasurementId) return;
    initGtag(gaMeasurementId);
  }, [analyticsConsent, gaMeasurementId]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setOnboardingOpen(false);
      return;
    }
    if (!isOnboardingRouteAllowed(location.pathname)) {
      setOnboardingOpen(false);
      return;
    }

    const userId = user?.id;
    const forcedOpen = consumeOnboardingForceOpen ? consumeOnboardingForceOpen(userId) : false;
    const completed = hasCompletedOnboarding ? hasCompletedOnboarding(userId) : false;
    if (forcedOpen || !completed) {
      setOnboardingOpen(true);
    }
  }, [authLoading, user, location.pathname, location.search, hasCompletedOnboarding, consumeOnboardingForceOpen]);

  // ✅ redirect root ή invalid lang (χωρίς loop / χωρίς null checks)
  if (normalizedPath === "/" || !seg || !isSupportedPathLang(seg)) {
    const stored = localStorage.getItem("language");
    const guess =
      stored || (typeof navigator !== "undefined" ? navigator.language : "el");
    const browserLang = pathLangFrom(guess);
    return <Navigate to={`/${browserLang}/`} replace />;
  }

  if (MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }

  const closeOnboarding = () => {
    setOnboardingOpen(false);
    if (markOnboardingCompleted && user?.id) {
      markOnboardingCompleted(user.id);
    }
  };

  const shouldRenderOnboardingTour =
    onboardingOpen && isOnboardingRouteAllowed(location.pathname);

  const handleAnalyticsAccept = () => {
    grantAnalytics();
    setAnalyticsConsent("granted");
    if (gaMeasurementId) {
      initGtag(gaMeasurementId);
    }
  };

  const handleAnalyticsReject = () => {
    denyAnalytics();
    setAnalyticsConsent("denied");
  };

  return (
    <HelmetProvider>

      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <CustomThemeProvider>
          <UserProvider>
            <ScrollToTop />
            {analyticsConsent === "granted" ? <AnalyticsTracker /> : null}
            {shouldRenderOnboardingTour ? (
              <OnboardingTour isOpen={onboardingOpen} onClose={closeOnboarding} />
            ) : null}
            <Routes>
              {/* ✅ Landing page */}
              <Route
                path=":country/"
                element={
                  <Layout>
                    <ProtectedRoute>
                      <ClientLandingPage />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              {/* ✅ Dashboard */}
              <Route
                path=":country/client/dashboard"
                element={
                  <Layout>
                    <ProtectedRoute>
                      <UserDashboard />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              <Route
                path=":country/admin/growth-dashboard"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth>
                      <AdminGrowthDashboard />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              {/* ✅ Redirect παλιού dashboard */}
              <Route
                path=":country/dashboard"
                element={<Navigate to="../client/dashboard" replace />}
              />

              {/* Public routes */}
              <Route path=":country/quick-recipe" element={<Layout><QuickRecipePage /></Layout>} />
              <Route path=":country/about-us" element={<Layout><AboutUs /></Layout>} />
              <Route path=":country/login" element={<Layout><Login /></Layout>} />
              <Route path=":country/register" element={<Layout><Register /></Layout>} />
              <Route path=":country/auth/mobile/:provider" element={<MobileSocialAuthBridge />} />
              <Route path=":country/privacy" element={<Layout><PrivacyPage /></Layout>} />
              <Route path=":country/reset-password" element={<Layout><ResetPassword /></Layout>} />
              <Route path=":country/reviews" element={<Layout><ReviewsPage /></Layout>} />
              <Route path=":country/search-recipes" element={<Layout><RecipeSearchPage /></Layout>} />
              <Route path=":country/budget-recipes" element={<Layout><BudgetRecipesPage /></Layout>} />
              <Route path=":country/recipe/:id/:slug" element={<Layout><RecipeDetails /></Layout>} />
              <Route path=":country/recipes/:slug" element={<Layout><RecipeDetails /></Layout>} />
              <Route path=":country/payment-success" element={<Layout><PaymentSuccess /></Layout>} />
              <Route path=":country/auth/facebook/callback" element={<FacebookCallback />} />
              <Route path=":country/plans" element={<Layout><PlansPage /></Layout>} />
              <Route path=":country/instacart/return" element={<Layout><ReturnFromInstacart /></Layout>} />

              {/* Meal Plan */}
              <Route
                path=":country/client/meal/plan"
                element={
                  <Layout>
                    <ProtectedRoute>
                      <MealPlan />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              {/* Client secured */}
              <Route
                path=":country/client/inventory/check"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth>
                      <CheckInventory />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              <Route
                path=":country/client/inventory/add/product/inventory"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth>
                      <AddProductInventory />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              <Route
                path=":country/client/household/profile"
                element={
                  <Layout>
                    <ProtectedRoute>
                      <HouseholdProfile />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              <Route
                path=":country/shopping-custom/list"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CustomShoppingList />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path=":country/client/select-shopping-list"
                element={
                  <Layout>
                    <ShoppingListsIndex />
                  </Layout>
                }
              />

              <Route
                path=":country/client/price-watchlist"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth requirePremium>
                      <PriceIntelligencePage />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              <Route
                path=":country/client/price-intelligence"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth>
                      <PriceIntelligencePage />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              <Route
                path=":country/price-watch"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth requirePremium>
                      <FollowedProductsPage />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              <Route
                path=":country/followed"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth requirePremium>
                      <FollowedProductsPage />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              <Route
                path=":country/client/shopping/list"
                element={
                  <Layout>
                    <ProtectedRoute>
                      <ShoppingList />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              {/* Subscription */}
              <Route
                path=":country/subscription"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth>
                      <SubscriptionPage />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              <Route
                path=":country/client/subscription"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth>
                      <SubscriptionPage />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              {/* ⚠️ αυτό δεν έχει :country στο path — το αφήνω όπως το είχες */}
              <Route
                path="/client/subscription-status"
                element={
                  <ProtectedRoute requireAuth>
                    <SubscriptionStatus />
                  </ProtectedRoute>
                }
              />

              {/* Recipes user area */}
              <Route
                path=":country/client/recipes-user"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth>
                      <RecipesUser />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              <Route
                path=":country/client/add-recipe"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth>
                      <AddRecipeUser />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              <Route
                path=":country/client/update-recipe/:id"
                element={
                  <Layout>
                    <ProtectedRoute requireAuth>
                      <UpdateRecipeUser />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              <Route path="*" element={<NotFound />} />
            </Routes>

            <MobileBottomNav />
            {analyticsConsent == null ? (
              <ConsentBanner
                onAccept={handleAnalyticsAccept}
                onReject={handleAnalyticsReject}
              />
            ) : null}
          </UserProvider>
        </CustomThemeProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
};

export default App;
